import React, {useContext, useEffect, useLayoutEffect, useRef, useState, useCallback, Suspense, lazy} from 'react'; // Добавил useCallback
import {BrowserRouter} from "react-router-dom";
import AppRouter from "./AppRouter";
import NavBar from "./components/layout/header/NavBar/NavBar";
import Navigation from "./components/layout/header/Navigation/Navigation";
import {observer} from "mobx-react-lite";
import {Context} from "./index";
import {checkAuth} from "./http/userApi";
import Loading from "./components/commons/Loading/Loading";
import {
    fetchArea,
    fetchBedroom,
    fetchBuilding,
    fetchFloor,
    fetchHeating,
    fetchParking,
    fetchRepair,
    fetchRoom
} from "./http/paramsApi";
import MobileBottomNav from "./components/layout/MobileBottomNav/MobileBottomNav/MobileBottomNav";
import CookieBannerManager from "./utils/CookieBannerManager/CookieBannerManager";
import { useSocket } from "./store/SocketContext";
import {getOwnerSessions} from "./http/chatApi";
import './styles/base/global.css'
import GlobalChatNotifications from "./components/chats/GlobalChatNotifications/GlobalChatNotifications";
import useSiteVersionSync from "./components/hooks/useSiteVersionSync/useSiteVersionSync";

const YMInitializerLazy = lazy(() =>
    import("react-yandex-metrika").then((m) => {
        const Comp = m.YMInitializer || m.default?.YMInitializer || m.default;
        if (!Comp) {
            // чтобы не падало в рантайме
            return { default: () => null };
        }
        return { default: Comp };
    })
);

const AppContent = ({ navBarRef, navigationRef, cookiesAccepted, handleAcceptCookies, metrikaOn }) => {
    useSiteVersionSync();

    return (
        <>
            {metrikaOn && (
                <Suspense fallback={null}>
                    <YMInitializerLazy accounts={[99134839]} options={{ webvisor: true }} />
                </Suspense>
            )}

            <NavBar ref={navBarRef} />
            <Navigation ref={navigationRef} />

            <GlobalChatNotifications />

            <AppRouter />
            <MobileBottomNav />

            <CookieBannerManager
                cookiesAccepted={cookiesAccepted}
                onAccept={handleAcceptCookies}
            />
        </>
    );
};

const App = observer(() => {
    // <<<--- 2. Добавляем chat в контекст
    const {user, add, helper, chat} = useContext(Context);
    const [isLoading, setIsLoading] = useState(true);

    // <<<--- 3. Инициализируем сокет
    const socket = useSocket();

    const navBarRef = useRef(null);
    const navigationRef = useRef(null);

    const [cookiesAccepted, setCookiesAccepted] = useState(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem("cookie_consent") === "true";
        }
        return false;
    });

    const [metrikaOn, setMetrikaOn] = useState(false);

    useEffect(() => {
        if (!cookiesAccepted) {
            setMetrikaOn(false);
            return;
        }

        const enable = () => {
            // чуть позже, чтобы не мешать LCP/TTI
            setTimeout(() => setMetrikaOn(true), 2500);
        };

        if (document.readyState === "complete") {
            enable();
            return;
        }

        window.addEventListener("load", enable, { once: true });
        return () => window.removeEventListener("load", enable);
    }, [cookiesAccepted]);

    const handleAcceptCookies = () => {
        setCookiesAccepted(true);
    };

    // -------------------------------------------------------------------------
    // 🔥 НАЧАЛО: ГЛОБАЛЬНАЯ ЛОГИКА СОКЕТОВ (Перенесено из ChatsPage)
    // -------------------------------------------------------------------------

    // Обработчик обновлений списка чатов (обернут в useCallback для стабильности)
    const handleChatListUpdate = useCallback((data) => {
        console.log(`🔔 GLOBAL APP: Получено обновление списка чатов`, data);

        const sessionFromSocket = data.session;
        let unreadCount = 0;

        // Логика определения, чей счетчик нам нужен (Владельца или Гостя)
        const isCurrentUserOwner = user.isAuth && sessionFromSocket.ownerId === user.UserData.id;

        if (isCurrentUserOwner) {
            unreadCount = sessionFromSocket.ownerUnreadCount || 0;
        } else {
            unreadCount = sessionFromSocket.guestUnreadCount || 0;
        }

        const sessionToUpdate = {
            ...sessionFromSocket,
            unreadCount: unreadCount
        };

        // Обновляем MobX Store. Это обновит счетчики везде, где используется chat.sessions
        chat.updateSession(sessionToUpdate);

        // (Опционально) Здесь можно добавить звук уведомления или Toast
    }, [chat, user.isAuth, user.UserData?.id]);


    // Эффект подключения к комнате и подписки на события
    useEffect(() => {
        if (isLoading || !socket) return;

        const guestSessionId = localStorage.getItem("sessionId");

        let roomName = null;
        if (user.isAuth && user.UserData?.id) {
            roomName = `user-${user.UserData.id}`;
        } else if (guestSessionId) {
            roomName = `guest-${guestSessionId}`;
        }

        const join = () => {
            if (!roomName) return;
            socket.emit("joinRoom", { roomName });
            console.log(`✅ GLOBAL APP: (Re)joined room: ${roomName}`);
        };

        // ✅ сразу при маунте/смене комнаты
        join();

        // ✅ и каждый раз после реконнекта
        socket.on("connect", join);

        socket.on("chatListUpdate", handleChatListUpdate);

        return () => {
            socket.off("connect", join);
            socket.off("chatListUpdate", handleChatListUpdate);
        };
    }, [socket, isLoading, user.isAuth, user.UserData?.id, handleChatListUpdate, chat.sessions.length]);

    // -------------------------------------------------------------------------
    // 🛑 КОНЕЦ ЛОГИКИ СОКЕТОВ
    // -------------------------------------------------------------------------

    useEffect(() => {
        helper.setCookiesAccepted(cookiesAccepted);
    }, [cookiesAccepted, helper]);

    useEffect(() => {
        if (!("serviceWorker" in navigator)) return;

        // Важно: должен совпадать с файлом в /public
        navigator.serviceWorker
            .register("/sw.js")
            .then((reg) => console.log("✅ SW registered:", reg.scope))
            .catch((err) => console.error("❌ SW register error:", err));
    }, []);

    const initializeUser = async () => {
        try {
            const userData = await checkAuth();
            user.setIsAuth(true);
            user.setUserData(userData);
            return true;
        } catch (e) {
            user.setIsAuth(false);
            localStorage.removeItem("token");
            document.cookie = "refreshToken=; Max-Age=0; path=/";
            return false;
        }
    };

    const fetchChats = async () => {
        chat.setIsLoading(true);
        try {
            const data = await getOwnerSessions();
            chat.setSessions(data.sessions);
        } catch (e) {
            console.error(e.message);
        } finally {
            chat.setIsLoading(false);
        }
    };

    useEffect(() => {
        if (user.isAuth) {
            // Как только авторизовались — тащим чаты
            fetchChats();
        } else {
            // Как только вышли — трем данные
            chat.clearStore();
        }
    }, [user.isAuth]); // Реагирует на вход/выход

    useEffect(() => {
        const initializeApp = async () => {
            try {
                const checkPromise = initializeUser();

                const [
                    bedrooms, buildings, floors, rooms,
                    heatings, repairs, parkings, areas
                ] = await Promise.all([
                    fetchBedroom(), fetchBuilding(), fetchFloor(), fetchRoom(),
                    fetchHeating(), fetchRepair(), fetchParking(), fetchArea()
                ]);

                add.setBedrooms(bedrooms);
                add.setBuildings(buildings);
                add.setFloors(floors);
                add.setRooms(rooms);
                add.setHeatings(heatings);
                add.setRepairs(repairs);
                add.setParkings(parkings);
                add.setAreas(areas);

                await checkPromise;

                const hasGuestSession = localStorage.getItem('guestSessionId');
                if (user.isAuth || hasGuestSession) {
                    await fetchChats();
                }
            } catch (error) {
                console.error("Ошибка инициализации приложения:", error);
            } finally {
                user.setIsLoaded(true);
                setIsLoading(false);
            }
        };

        initializeApp();
    }, []);

    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.visibilityState === 'visible') {
                await initializeUser();
                // При возвращении на вкладку сокет обычно сам переподключается,
                // но если нужно форсировать обновление данных, можно добавить логику здесь.
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    useLayoutEffect(() => {
        if (isLoading) return;

        const navEl = navBarRef.current;
        const nav2El = navigationRef.current;
        if (!navEl || !nav2El) return;

        const update = () => {
            helper.setNavBarHeight(navEl.getBoundingClientRect().height || 0);
            helper.setNavigationHeight(nav2El.getBoundingClientRect().height || 0);
        };

        update(); // сразу

        const ro = new ResizeObserver(update);
        ro.observe(navEl);
        ro.observe(nav2El);

        window.addEventListener("resize", update);

        return () => {
            ro.disconnect();
            window.removeEventListener("resize", update);
        };
    }, [isLoading, helper]);

    if (isLoading) {
        return (
            <Loading/>
        );
    }

    return (
        <BrowserRouter>
            <AppContent
                navBarRef={navBarRef}
                navigationRef={navigationRef}
                cookiesAccepted={cookiesAccepted}
                handleAcceptCookies={handleAcceptCookies}
                metrikaOn={metrikaOn}
            />
        </BrowserRouter>
    );
});

export default App;